import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Home, Building2, DoorOpen, Clock } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import PropertyCard from "@/components/PropertyCard";
import RoomCard from "@/components/RoomCard";
import PropertyMap, { TYPE_COLORS } from "@/components/PropertyMap";
import LandlordContact from "@/components/LandlordContact";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LANDLORD } from "@/data/landlord";

const TYPES = ["All", "Availability", "HMO", "House", "Flat", "Studio", "Garage"];
const STATUSES = ["All", "Available", "Pending Lease", "Rented"];

export default function Catalog() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      const params = {};
      if (type === "Availability") params.availability = "1";
      else if (type !== "All") params.type = type;
      if (status !== "All") params.status = status;
      if (search.trim()) params.search = search.trim();
      if (sort !== "newest") params.sort = sort;
      api
        .get("/properties", { params })
        .then((r) => setProperties(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [type, status, search, sort]);

  const houseTypes = ["House", "HMO"];
  const flatTypes = ["Flat", "Studio"];
  const homesAvailable = properties.filter((p) => houseTypes.includes(p.type) && p.status === "Available").length;
  const flatsAvailable = properties.filter((p) => flatTypes.includes(p.type) && p.status === "Available").length;
  const roomsAvailable = properties.reduce(
    (n, p) => n + (p.rooms || []).filter((r) => r.status === "Available").length,
    0
  );

  return (
    <div className="min-h-screen bg-[#EFF0F2] dark:bg-[#12100E]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="py-14 lg:py-24 animate-fade-up">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#A05A32] dark:text-[#D9773F] mb-5">
            A Property Portfolio · Ricky Rooms Lettings
          </p>
          <h1 className="max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-serif font-light tracking-tight leading-[1.08] text-[#1A1715] dark:text-[#F7F4F0]">
            Quality homes, flats & rooms, <span className="italic text-[#A05A32] dark:text-[#D9773F]">professionally managed</span> and available directly through Ricky Rooms Lettings.
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-[#57524E] dark:text-[#C7BEB5] leading-relaxed">
            Explore our property portfolio, discover your next home, and get in touch with our team.
            Straightforward letting, clear communication and a professional service — with no unnecessary fees or third-party agents.
          </p>
          <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
            <div className="flex items-center gap-3">
              <Home className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
              <div>
                <p data-testid="hero-homes-count" className="font-serif text-2xl text-[#1A1715] dark:text-[#F7F4F0]">{homesAvailable}</p>
                <p className="text-xs text-[#8A837C] dark:text-[#8E867E] uppercase tracking-wider">Homes available</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
              <div>
                <p data-testid="hero-flats-count" className="font-serif text-2xl text-[#1A1715] dark:text-[#F7F4F0]">{flatsAvailable}</p>
                <p className="text-xs text-[#8A837C] dark:text-[#8E867E] uppercase tracking-wider">Flats available</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DoorOpen className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
              <div>
                <p data-testid="hero-rooms-count" className="font-serif text-2xl text-[#1A1715] dark:text-[#F7F4F0]">{roomsAvailable}</p>
                <p className="text-xs text-[#8A837C] dark:text-[#8E867E] uppercase tracking-wider">Rooms available</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
              <div>
                <p className="font-serif text-2xl text-[#1A1715] dark:text-[#F7F4F0]">&lt; 2h</p>
                <p className="text-xs text-[#8A837C] dark:text-[#8E867E] uppercase tracking-wider">Response time</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-[#E4DBD0] dark:border-[#352F2B] pt-8 pb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A837C]" />
              <Input
                data-testid="filter-search-input"
                placeholder="Search by name or neighbourhood..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 rounded-full bg-white dark:bg-[#1A1715] border-[#E4DBD0] dark:border-[#352F2B]"
              />
            </div>
            <div className="flex gap-3">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="filter-availability-select" className="w-[160px] h-11 rounded-full bg-white dark:bg-[#1A1715] border-[#E4DBD0] dark:border-[#352F2B]">
                  <SelectValue placeholder="Availability" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s === "All" ? "All statuses" : s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="filter-price-sort" className="w-[170px] h-11 rounded-full bg-white dark:bg-[#1A1715] border-[#E4DBD0] dark:border-[#352F2B]">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="rent_asc">Price: low to high</SelectItem>
                  <SelectItem value="rent_desc">Price: high to low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {TYPES.map((t) => (
              <button
                key={t}
                data-testid={`filter-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setType(t)}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                  type === t
                    ? "bg-[#1A1715] text-[#EFF0F2] border-[#1A1715] dark:bg-[#F7F4F0] dark:text-[#12100E] dark:border-[#F7F4F0]"
                    : "border-[#E4DBD0] dark:border-[#352F2B] text-[#57524E] dark:text-[#C7BEB5] hover:border-[#A05A32] hover:text-[#A05A32] dark:hover:text-[#D9773F]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>

        <section className="py-10 lg:py-14" data-testid="property-grid-section">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] overflow-hidden">
                  <div className="aspect-[16/10] bg-[#ECE5DD] dark:bg-[#231F1C] animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-5 w-2/3 rounded bg-[#ECE5DD] dark:bg-[#231F1C] animate-pulse" />
                    <div className="h-4 w-1/2 rounded bg-[#ECE5DD] dark:bg-[#231F1C] animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div data-testid="property-grid-empty" className="py-24 text-center">
              <p className="font-serif text-3xl text-[#1A1715] dark:text-[#F7F4F0]">No residences match your search</p>
              <p className="mt-2 text-[#8A837C] dark:text-[#8E867E]">Try clearing a filter or broadening your search.</p>
            </div>
          ) : type === "Availability" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="availability-rooms-grid">
              {properties.flatMap((p) =>
                (p.rooms || [])
                  .filter((r) => r.status === "Available" || r.status === "Pending Lease")
                  .map((r) => (
                    <RoomCard key={r.id} room={r} propertyTitle={p.title} propertyId={p.id} />
                  ))
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          )}
        </section>

        <LandlordContact />

        <section className="pb-8" data-testid="catalog-map-section">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#A05A32] dark:text-[#D9773F] mb-3">
            Where We Are
          </p>
          <h2 className="text-2xl sm:text-3xl font-serif text-[#1A1715] dark:text-[#F7F4F0] mb-6">
            Find our properties on the map
          </h2>
          <PropertyMap properties={properties} testId="catalog-map" height="h-[420px]" />
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2" data-testid="catalog-map-legend">
            {Object.entries(TYPE_COLORS).map(([t, c]) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs text-[#57524E] dark:text-[#C7BEB5]">
                <span className="h-3 w-3 rounded-full inline-block" style={{ background: c }} />
                {t}
              </span>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-[#E4DBD0] dark:border-[#352F2B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <img src="/logo.png" alt="Ricky Rooms Lettings" className="h-14 w-auto" />
            <p className="mt-1 text-sm text-[#8A837C] dark:text-[#8E867E]">{LANDLORD.office}</p>
            <p className="mt-1 text-sm text-[#8A837C] dark:text-[#8E867E]">{LANDLORD.emergency}</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#57524E] dark:text-[#C7BEB5]">
            <a href={LANDLORD.phoneHref} className="hover:text-[#A05A32] dark:hover:text-[#D9773F] transition-colors">{LANDLORD.phone}</a>
            <Link data-testid="footer-admin-link" to="/admin/login" className="hover:text-[#A05A32] dark:hover:text-[#D9773F] transition-colors">Owner Portal</Link>
          </div>
        </div>
        <div className="border-t border-[#EDE6DE] dark:border-[#28231F] py-5 text-center text-xs text-[#8A837C] dark:text-[#8E867E]">
          © {new Date().getFullYear()} {LANDLORD.name}. All residences privately owned & managed.
        </div>
      </footer>
    </div>
  );
}
